<template>
  <div class="ui-badge" :class="[
    typeClass,
    variantClass,
    sizeClass,
    rounded
  ]" v-bind="$attrs">
    <slot>
      {{ label }}
    </slot>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'

const { size = 'md', variant = '', type = '' } = defineProps<{
  label?: string
  size?: 'xs' | 'sm' | 'md' | 'lg'
  type?: 'primary' | 'info' | 'success' | 'danger' | 'warning' | 'neutral'
  variant?: 'solid' | 'outline'
  rounded?: string
}>()

const sizeClass = computed(() => {
  return {
    xs: 'text-xs px-1.5 py-0.5',
    sm: 'text-xs px-2 py-1',
    md: 'text-sm px-2 py-1',
    lg: 'text-sm px-2.5 py-1.5'
  }[size]
})

const variantClass = computed(() => {
  return {
    'outline': 'badge-outline',
    'solid': 'badge-solid',
  }[variant] || ''
})

const typeClass = computed(() => {
  if (!type) return 'primary'
  const tp = type.toLowerCase()
  return ['primary', 'info', 'success', 'warning', 'danger', 'neutral'].includes(tp) ? tp : 'primary'
})
</script>

<style scoped lang="scss">
.ui-badge {
  @apply inline-block box-border text-sm text-center my-auto font-medium;

  &.primary {
    @apply bg-[--c-primary-200] text-[--c-primary-800];

    &.badge-solid {
      @apply bg-[--c-primary-500] text-neutral-100 dark:bg-[--c-primary-400] dark:text-neutral-800;
    }

    &.badge-outline {
      @apply border border-solid border-[--c-primary-500] dark:text-[--c-primary-500] bg-transparent;
    }
  }

  &.info {
    @apply bg-[#D1E6FF] text-[#2C91F9] dark:bg-[#1E2A3B];

    &.badge-solid {
      @apply bg-blue-500 dark:bg-blue-400 text-neutral-100 dark:text-neutral-800;
    }

    &.badge-outline {
      @apply border border-solid border-[#1D9BF0] text-[#1D9BF0] bg-transparent;
    }
  }

  &.success {
    @apply bg-[#C8F9E5] text-[#17AC6C] dark:bg-[#1C3329];

    &.badge-solid {
      @apply bg-green-500 dark:bg-green-400 text-neutral-100 dark:text-neutral-800;
    }

    &.badge-outline {
      @apply border border-solid border-[#0E563A] dark:border-[#17AC6C] dark:text-[#17AC6C] text-[#0E563A] bg-transparent;
    }
  }

  &.danger {
    @apply bg-[#FDEBF1] text-[#FF4980] dark:bg-[#3F242C];

    &.badge-solid {
      @apply bg-red-500 dark:bg-red-400 text-neutral-100 dark:text-neutral-800;
    }

    &.badge-outline {
      @apply border border-solid border-[#FF4980] text-[#FF4980] bg-transparent;
    }
  }

  &.warning {
    @apply bg-[#FDF5E8] text-[#FCA927] dark:bg-[#3F321F];

    &.badge-solid {
      @apply bg-yellow-500 dark:bg-yellow-400 text-neutral-100 dark:text-neutral-800;
    }

    &.badge-outline {
      @apply border border-solid border-[#FCA927] bg-transparent;
    }
  }

  &.neutral {
    @apply bg-[#E1E1E1] text-[#434343] dark:bg-neutral-700 dark:text-neutral-300;

    &.badge-outline {
      @apply border border-solid border-[#434343] dark:text-[#434343] bg-transparent;
    }
  }

}
</style>